import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from "@angular/core";
import { StateService } from "@uirouter/core";
import { merge, orderBy } from "lodash";
import { forkJoin } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { environment as appEnv } from "../../environments/environment";
import { ChartHelperService } from "../chart/chart-helper.service";
import { TicksAgoService } from "../chart/ticks-ago.service";
import { CSSColorService } from "../css-color/css-color.service";
import { sampleDeploysWithErrors } from "../deploys-with-errors/sample-deploys-with-errors";
import { IgnoreNullsSortService } from "../ignore-nulls-sort/ignore-nulls-sort.service";
import { DeployService } from "../models/deploy.service";
import { MultiProjectRoutesStatsService } from "../models/multi-project-routes-stats.service";
import { ProjectService } from "../models/project.service";
import { StatsmanService } from "../models/statsman.service";
import { RememberService } from "../remember/remember.service";
import { RoundTimeService } from "../round-time/round-time.service";
import { StatsNormalizerService } from "../stats-normalizer/stats-normalizer.service";
import { StringToColorService } from "../string-to-color/string-to-color.service";
import { TimeDurAgoService } from "../time-dur-ago/time-dur-ago.service";
import { sum } from "../utils/sum";
import { timeSample } from "../utils/time-sample";

interface IStat {
  count: number[];
  time: number[];
}

interface IMetrics {
  accepted: IStat;
  rateLimited: IStat;
  rejected: IStat;
  overQuota: IStat;
  deploys: IStat;
  newGroups: IStat;
}

@Component({
  selector: "ab-account-projects",
  templateUrl: "./account-projects.component.html",
})
export class AccountProjectsComponent implements OnInit, OnChanges {
  @Input() public project: any;
  @Input() public dur: string;
  @Input() public lastN: number;
  @Input() public user: any;
  @Input() public featureDisabled: boolean;
  @Input() public hasNoErrors: boolean;
  @Input() public hasNoDeploys: boolean;
  @Input() public hasNoRequests: boolean;
  @Output() public loadingChanged = new EventEmitter<number>();

  public appEnv = appEnv;
  public column: string;
  public sortDesc: boolean;
  public loadingStatsmanData: boolean;
  public loadingDeployStats: boolean;
  public loadingPerformanceStats: boolean;
  public projects: any;
  public errorLinechartData: any;
  public deployLinechartData: any;
  public deploysWithErrors: any;
  public deployCountData: any;
  public errorCardsData: any;
  public performanceCardsData: any;
  public performanceLinechartData: any;
  public openedDeployList: boolean;

  private MINI_BAR_CHART_OPTIONS = merge(
    {},
    this.chartHelperService.defaultMiniBarChartOptions(),
    {
      onClick: (_event: MouseEvent, activeElements: any[]) => {
        const date = activeElements[0]._model.label;
        const projectId = activeElements[0]._chart.config.data.projectId;
        this.chartHelperService.removeTooltip();

        this.$state.go(
          "project-errors",
          {
            projectId,
            filters: {
              resolved: "any",
              on: Math.round(date.setUTCHours(0, 0, 0, 0) / 1000),
            },
          },
          {
            inherit: false,
          },
        );
      },
      onHover: (event: MouseEvent, activeElements: any[]) => {
        const canvas = event.target as HTMLCanvasElement;
        canvas.style.cursor = activeElements[0] ? "pointer" : "default";
      },
    },
  );
  private PROJECT_BATCH_SIZE = 8;
  private REMEMBER_SORT_COLUMN = "accountProjects.sortColumn";
  private REMEMBER_SORT_DESC = "accountProjects.sortDesc";
  private ticks: Date[];

  constructor(
    private projectService: ProjectService,
    private rememberService: RememberService,
    private statsmanService: StatsmanService,
    private stringToColorService: StringToColorService,
    private roundTimeService: RoundTimeService,
    private timeDurAgoService: TimeDurAgoService,
    private cssColorService: CSSColorService,
    private deployService: DeployService,
    private statsNormalizerService: StatsNormalizerService,
    private chartHelperService: ChartHelperService,
    private ticksAgoService: TicksAgoService,
    private multiProjectRoutesStatsService: MultiProjectRoutesStatsService,
    private $state: StateService,
    private ignoreNullsSortService: IgnoreNullsSortService,
  ) {}

  public ngOnInit() {
    this.column = this.rememberService.get(
      this.REMEMBER_SORT_COLUMN,
      "name",
    ) as string;
    this.sortDesc = this.rememberService.get(
      this.REMEMBER_SORT_DESC,
      this.defaultSortDesc(this.column),
    ) as boolean;
    this.loadingStatsmanData = false;
    this.loadingDeployStats = false;
    this.loadingPerformanceStats = false;
    this.openedDeployList = false;

    // Forgetting last-viewed project means this view becomes the default.
    this.rememberService.forget("projects.lastViewed");
  }

  public ngOnChanges() {
    if (this.featureDisabled == null) {
      return;
    }

    this.ticks = this.ticksAgoService.ticksFor(this.dur, this.lastN);

    if (this.featureDisabled) {
      this.generateSampleData();
      return;
    }

    if (this.projects) {
      this.loadStats();
    } else {
      this.projectService.query().subscribe((data: any) => {
        this.projects = data.projects || [];
        this.loadStats();
      });
    }
  }

  public plotProject(project: any, $event: any) {
    const node = $event.currentTarget;
    node.style.backgroundColor = project.errorLinechartData.backgroundColor;

    const plotted =
      project.errorLinechartData.showLine ||
      project.deployLinechartData.showLine ||
      project.performanceLinechartData.showLine;

    if (plotted) {
      project.errorLinechartData.showLine = false;
      project.deployLinechartData.showLine = false;
      project.performanceLinechartData.showLine = false;
      node.style.backgroundColor = null;
    } else {
      project.errorLinechartData.showLine = true;
      project.deployLinechartData.showLine = true;
      project.performanceLinechartData.showLine = true;
      node.style.backgroundColor = project.errorLinechartData.backgroundColor;
    }

    this.rememberService.set(
      `linechart.${project.id}.plot`,
      project.errorLinechartData.showLine,
    );
    this.buildLinechartData(this.projects);
    this.buildPerformanceLinechartData(this.projects);
  }

  public sortBy(column: string) {
    this.sortDesc =
      column === this.column ? !this.sortDesc : this.defaultSortDesc(column);
    this.column = column;

    this.rememberService.set(this.REMEMBER_SORT_DESC, this.sortDesc);
    this.rememberService.set(this.REMEMBER_SORT_COLUMN, this.column);

    this.projects = this.sortProjects(
      this.projects,
      this.column,
      this.sortDesc,
    );
  }

  public trackBy(_index: number, project: any) {
    return project.id;
  }

  private loadStats() {
    this.loadDeployStats().subscribe();
    forkJoin([
      this.loadStatsmanData({ dur: this.dur, last_n: this.lastN }),
      this.loadPerformanceStats(),
    ]).subscribe(() => {
      this.projects = this.sortProjects(
        this.projects,
        this.column,
        this.sortDesc,
      );
    });
  }

  private sortProjects(projects: any[], column: string, sortDesc: boolean) {
    return this.ignoreNullsSortService.sort(
      orderBy(projects, [column], [sortDesc ? "desc" : "asc"]),
      column,
    );
  }

  private loadStatsmanData(params: any) {
    this.loadingStatsmanData = true;
    this.loadingChanged.emit(1);

    const observables = [];
    for (
      let i = 0, j = this.projects.length;
      i < j;
      i += this.PROJECT_BATCH_SIZE
    ) {
      const projectBatch = this.projects.slice(i, i + this.PROJECT_BATCH_SIZE);
      observables.push(this.loadProjectStats(projectBatch, params));
    }

    return forkJoin(observables).pipe(
      tap(() => {
        this.buildLinechartData(this.projects);
        this.buildCardsData(this.projects);
      }),
      finalize(() => {
        this.loadingStatsmanData = false;
        this.loadingChanged.emit(-1);
      }),
    );
  }

  private loadDeployStats() {
    this.loadingDeployStats = true;
    this.loadingChanged.emit(1);

    return this.deployService
      .userQuery({
        group_created_count__gte: 1,
        created_at__gte: this.timeDurAgoService.timeFor(this.dur, this.lastN),
      })
      .pipe(
        tap((deploysWithErrors: any) => {
          if (deploysWithErrors.count === 0) {
            this.openedDeployList = false;
          }
          this.deploysWithErrors = deploysWithErrors;
        }),
        finalize(() => {
          this.loadingDeployStats = false;
          this.loadingChanged.emit(-1);
        }),
      );
  }

  private loadPerformanceStats() {
    this.loadingPerformanceStats = true;
    this.loadingChanged.emit(1);

    const period = (() => {
      switch (this.dur.split("/")[1]) {
        case "1d":
          return "day";
        case "1h":
          return "hour";
        case "1m":
          return "minute";
      }
    })();

    const params = {
      period,
      time__gte: this.roundTimeService.timestampFor(
        this.timeDurAgoService.timeFor(this.dur, this.lastN),
        this.dur,
      ),
      project_ids: this.projects.map((p: any) => p.id),
    };

    return this.multiProjectRoutesStatsService.get(params).pipe(
      tap((data: any) => {
        this.projects.forEach((project: any) => {
          this.assignProjectPerformanceStats(
            project,
            data[project.id.toString()],
          );
        });

        this.buildPerformanceLinechartData(this.projects);
        this.buildPerformanceCardsData(this.projects);
      }),
      finalize(() => {
        this.loadingPerformanceStats = false;
        this.loadingChanged.emit(-1);
      }),
    );
  }

  private assignProjectPerformanceStats(project: any, data: any) {
    project.requestCount = sum(data.total.count);
    project.requestCountList = this.populateEmptyDaysWithZeros(
      data.total.count,
      data.time,
    );

    project.p50 = sum(data.total.p50);
    project.p50List = this.populateEmptyDaysWithZeros(
      data.total.p50,
      data.time,
    );

    project.p95 = sum(data.total.p95);
    project.p95List = this.populateEmptyDaysWithZeros(
      data.total.p95,
      data.time,
    );

    project.apdexScore = this.calculateApdexScore(data.total);
    project.apdexScoreList = this.populateEmptyDaysWithZeros(
      this.calculateApdexScoreList(data.total, data.time),
      data.time,
    );

    project.successCount = sum(data.success.count);
    project.redirectionCount = sum(data.redirection.count);
    project.clientErrorCount = sum(data.clientError.count);
    project.serverErrorCount = sum(data.serverError.count);

    project.apmNoticeCount = sum(data.noticeCount);
    project.apmNoticeCountList = this.populateEmptyDaysWithZeros(
      data.noticeCount,
      data.time,
    );

    project.performanceLinechartData =
      this.buildProjectPerformanceLinechartData(project, data);
  }

  private calculateApdexScore(data: any) {
    if (!data.satisfied || !data.tolerating || !data.frustrated) {
      return 0;
    }

    const satisfied = sum(data.satisfied);
    const tolerating = sum(data.tolerating);
    const frustrated = sum(data.frustrated);
    const total = satisfied + tolerating + frustrated;
    return (satisfied + tolerating / 2) / total;
  }

  private calculateApdexScoreList(data: any, time: string[]) {
    if (!data.satisfied || !data.tolerating || !data.frustrated) {
      return [];
    }

    const apdexScoreList = [];
    for (let i = 0; i < time.length; i++) {
      const total = data.satisfied[i] + data.tolerating[i] + data.frustrated[i];
      apdexScoreList.push((data.satisfied[i] + data.tolerating[i] / 2) / total);
    }
    return apdexScoreList;
  }

  private populateEmptyDaysWithZeros(stats: number[], time: string[]) {
    return this.statsNormalizerService.populateEmptyDaysWithZeros(
      stats,
      time,
      this.dur,
      this.lastN,
    );
  }

  private buildLinechartData(projects: any) {
    this.errorLinechartData = {
      data: {
        labels: this.ticks,
        datasets: projects
          .filter((project: any) => project.errorLinechartData.showLine)
          .map((project: any) => project.errorLinechartData),
      },
      options: this.chartHelperService.defaultChartOptions(),
    };

    this.deployLinechartData = {
      data: {
        labels: this.ticks,
        datasets: projects
          .filter((project: any) => project.deployLinechartData.showLine)
          .map((project: any) => project.deployLinechartData),
      },
      options: this.chartHelperService.defaultChartOptions(),
    };
  }

  private buildPerformanceLinechartData(projects: any[]) {
    this.performanceLinechartData = {
      data: {
        labels: this.ticks,
        datasets: projects
          .filter((project: any) => project.performanceLinechartData.showLine)
          .map((project: any) => project.performanceLinechartData),
      },
      options: this.chartHelperService.defaultChartOptions(),
    };
  }

  private buildCardsData(projects: any) {
    this.errorCardsData = projects.map((project: any) => {
      return {
        unresolvedGroups: project.groupUnresolvedCount,
        deploys: project.deployCount,
        deployCountList: project.deployCountList,
        id: project.id,
        lastNoticeAt: project.lastNoticeAt,
        newGroups: project.newGroupCount,
        newGroupCountList: project.newGroupCountList,
      };
    });
    this.deployCountData = projects.map((project: any) => {
      return project.deployCountList;
    });
  }

  private buildPerformanceCardsData(projects: any) {
    this.performanceCardsData = projects.map((project: any) => {
      return {
        requestCount: project.requestCount,
        requestCountList: project.requestCountList,
        successCount: project.successCount,
        redirectionCount: project.redirectionCount,
        clientErrorCount: project.clientErrorCount,
        serverErrorCount: project.serverErrorCount,
        apmNoticeCount: project.apmNoticeCount,
        apmNoticeCountList: project.apmNoticeCountList,
      };
    });
  }

  private buildMetricNames(projects: any[]): string[] {
    const flatten = (array: any[]): any[] => {
      return array.reduce((accumulator: string[], currentValue: string) => {
        return accumulator.concat(currentValue);
      }, []);
    };

    return flatten(
      projects.map((p: any) => {
        return [
          `projects.${p.id}.notices`,
          `projects.${p.id}.rate_limited`,
          `projects.${p.id}.deploys`,
          `projects.${p.id}.rejected`,
          `projects.${p.id}.over_quota`,
          `projects.${p.id}.groups.created`,
        ];
      }),
    );
  }

  private buildProjectData(data: any, projects: any[]) {
    projects.forEach((p: any) => {
      const metrics = {
        accepted: data[`projects.${p.id}.notices`],
        rateLimited: data[`projects.${p.id}.rate_limited`],
        rejected: data[`projects.${p.id}.rejected`],
        overQuota: data[`projects.${p.id}.over_quota`],
        deploys: data[`projects.${p.id}.deploys`],
        newGroups: data[`projects.${p.id}.groups.created`],
      };

      this.buildProjectLinechartData(p, metrics);
      this.buildProjectBarchartData(p, metrics);
      this.buildProjectTableData(p, metrics);
    });
  }

  private loadProjectStats(projects: any[], params: any) {
    const statsmanParams = {
      dur: params.dur,
      last_n: params.last_n,
      name: this.buildMetricNames(projects),
      projectId: projects[0].id,
      type: "counter",
    };

    return this.statsmanService.query(statsmanParams).pipe(
      tap((data: any) => {
        this.buildProjectData(data, projects);
      }),
    );
  }

  private defaultSortDesc(column: string) {
    // For the default order, users most likely want to see the largest or
    // most recent first, except for alpha where A-Z makes more sense.
    return column !== "name";
  }

  private generateSampleData() {
    this.projects = [
      this.generateSampleProject(1, "Frontend (sample)", "#00c853"),
      this.generateSampleProject(2, "Backend (sample)", "#939393"),
    ];

    this.errorCardsData = [
      {
        deploys: this.generateSampleNumber(100),
        deployCountList: this.generateSampleCounts(90),
        newGroups: this.generateSampleNumber(100),
        newGroupCountList: this.generateSampleCounts(90),
        unresolvedGroups: this.generateSampleNumber(10),
        lastNoticeAt: Date.now(),
      },
      {
        deploys: this.generateSampleNumber(100),
        deployCountList: this.generateSampleCounts(90),
        newGroups: this.generateSampleNumber(100),
        newGroupCountList: this.generateSampleCounts(90),
        acceptedNotices: this.generateSampleNumber(10),
        acceptedNoticeCountList: this.generateSampleCounts(90),
        rateLimitedNotices: this.generateSampleNumber(5),
        rateLimitedNoticeCountList: this.generateSampleCounts(90),
        unresolvedGroups: this.generateSampleNumber(2),
        lastNoticeAt: Date.now(),
      },
    ];

    this.deploysWithErrors = sampleDeploysWithErrors;
    this.deployCountData = [
      [10, 20, 30, 40],
      [12, 11, 33, 22],
    ];

    this.performanceCardsData = [
      {
        requestCount: this.generateSampleNumber(200000),
        requestCountList: this.generateSampleCounts(90),
        successCount: this.generateSampleNumber(50000),
        redirectionCount: this.generateSampleNumber(50000),
        clientErrorCount: this.generateSampleNumber(50000),
        serverErrorCount: this.generateSampleNumber(50000),
        apmNoticeCount: this.generateSampleNumber(10000),
        apmNoticeCountList: this.generateSampleCounts(90),
      },
      {
        requestCount: this.generateSampleNumber(200000),
        requestCountList: this.generateSampleCounts(90),
        successCount: this.generateSampleNumber(50000),
        redirectionCount: this.generateSampleNumber(50000),
        clientErrorCount: this.generateSampleNumber(50000),
        serverErrorCount: this.generateSampleNumber(50000),
        apmNoticeCount: this.generateSampleNumber(10000),
        apmNoticeCountList: this.generateSampleCounts(90),
      },
    ];

    this.errorLinechartData = this.generateSampleLinechartData();
    this.deployLinechartData = this.generateSampleLinechartData();
    this.performanceLinechartData = this.generateSampleLinechartData();
    this.hasNoErrors = false;
    this.hasNoDeploys = false;
    this.hasNoRequests = false;
  }

  private generateSampleLinechartData() {
    return {
      data: {
        labels: timeSample(),
        datasets: [
          {
            label: "Frontend (sample)",
            backgroundColor: "#00c853",
            borderColor: "#00c853",
            pointHoverRadius: 2,
            showLine: true,
            data: this.generateSampleCounts(90),
          },
          {
            label: "Backend (sample)",
            backgroundColor: "#939393",
            borderColor: "#939393",
            pointHoverRadius: 2,
            showLine: true,
            data: this.generateSampleCounts(90),
          },
        ],
      },
      options: this.chartHelperService.defaultChartOptions(),
    };
  }

  private generateSampleProject(id: number, name: string, color: string): any {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dates = [Date.now(), yesterday];

    return {
      id,
      name,
      deployCount: this.generateSampleNumber(50),
      deployCountList: this.generateSampleCounts(90),
      groupUnresolvedCount: this.generateSampleNumber(50),
      lastNoticeAt: dates[this.generateSampleNumber(1)],
      newGroupCount: this.generateSampleNumber(100),
      newGroupCountList: this.generateSampleCounts(90),
      acceptedNoticeCountList: this.generateSampleCounts(90),
      rateLimitedNoticeCountList: this.generateSampleCounts(90),
      acceptedNoticeCount: this.generateSampleNumber(50),
      rateLimitedNoticeCount: this.generateSampleNumber(50),
      p50: this.generateSampleNumber(1000),
      p50List: this.generateSampleCounts(90),
      p95: this.generateSampleNumber(10000),
      p95List: this.generateSampleCounts(90),
      requestCount: this.generateSampleNumber(200000),
      requestCountList: this.generateSampleCounts(90),
      apdexScore: Math.random(),
      apdexScoreList: this.generateSampleCounts(90),
      barchartConfig: {
        data: {
          labels: timeSample().slice(0, 10),
          datasets: [
            {
              label: "Accepted errors",
              data: this.generateSampleCounts(10),
              backgroundColor: this.cssColorService.getColor("--ab-grey-700"),
              borderColor: this.cssColorService.getColor("--ab-grey-700"),
              hoverBackgroundColor:
                this.cssColorService.getColor("--ab-grey-400"),
              hoverBorderColor: this.cssColorService.getColor("--ab-grey-400"),
              minBarLength: 1,
              barPercentage: 0.85,
              categoryPercentage: 1,
            },
          ],
        },
        options: this.MINI_BAR_CHART_OPTIONS,
      },
      errorLinechartData: { showLine: true, backgroundColor: color },
      performanceLinechartData: { showLine: true, backgroundColor: color },
    };
  }

  private generateSampleNumber(max: number): number {
    return Math.round(Math.random() * max);
  }

  private generateSampleCounts(n: number): number[] {
    const counts = [];

    for (let i = 0; i < n; i++) {
      counts.push(this.generateSampleNumber(1000));
    }

    return counts;
  }

  private buildProjectLinechartData(project: any, metrics: IMetrics) {
    const showLineError = this.showLine(project, metrics.accepted.count);
    const showLineDeploys = this.showLine(project, metrics.deploys.count);
    const colorName = this.stringToColorService.colorFor(project.name);
    const colorValue = this.cssColorService.getColor("--ab-" + colorName);

    project.errorLinechartData = {
      label: project.name,
      backgroundColor: colorValue,
      borderColor: colorValue,
      colorName,
      pointHoverRadius: showLineError ? 2 : 0,
      showLine: showLineError,
      data: metrics.accepted.count,
    };

    project.deployLinechartData = {
      label: project.name,
      backgroundColor: colorValue,
      borderColor: colorValue,
      colorName,
      pointHoverRadius: showLineDeploys ? 2 : 0,
      showLine: showLineDeploys,
      data: metrics.deploys.count,
    };

    this.rememberService.set(
      this.linechartPlotRememberKey(project),
      project.errorLinechartData.showLine,
    );
  }

  private buildProjectPerformanceLinechartData(project: any, data: any) {
    const showLine = this.showLine(project, data.total.count);
    const colorName = this.stringToColorService.colorFor(project.name);
    const colorValue = this.cssColorService.getColor("--ab-" + colorName);

    return {
      label: project.name,
      backgroundColor: colorValue,
      borderColor: colorValue,
      colorName,
      pointHoverRadius: showLine ? 2 : 0,
      showLine,
      data: this.populateEmptyDaysWithZeros(data.total.count, data.time),
    };
  }

  private linechartPlotRememberKey(project: any) {
    return `linechart.${project.id}.plot`;
  }

  private showLine(project: any, counts: number[]) {
    return this.rememberService.get(
      this.linechartPlotRememberKey(project),
      Math.max.apply(null, counts.slice(-10)) > 0,
    );
  }

  // Barcharts use only the last 10 segments of data.
  private buildProjectBarchartData(project: any, metrics: IMetrics) {
    project.barchartConfig = {
      data: {
        projectId: project.id,
        labels: this.ticks.slice(-10),
        datasets: [
          {
            label: "Accepted errors",
            data: metrics.accepted.count.slice(-10),
            backgroundColor: this.cssColorService.getColor("--ab-grey-700"),
            borderColor: this.cssColorService.getColor("--ab-grey-700"),
            hoverBackgroundColor:
              this.cssColorService.getColor("--ab-grey-400"),
            hoverBorderColor: this.cssColorService.getColor("--ab-grey-400"),
            minBarLength: 1,
            barPercentage: 0.85,
            categoryPercentage: 1,
          },
          {
            label: "Rate limited errors",
            data: metrics.rateLimited.count.slice(-10),
            backgroundColor: this.cssColorService.getColor("--ab-red-400"),
            borderColor: this.cssColorService.getColor("--ab-red-400"),
            hoverBackgroundColor: this.cssColorService.getColor("--ab-red-300"),
            hoverBorderColor: this.cssColorService.getColor("--ab-red-300"),
            barPercentage: 0.85,
            categoryPercentage: 1,
          },
          {
            label: "Exceeded quota errors",
            data: metrics.overQuota.count.slice(-10),
            backgroundColor: this.cssColorService.getColor("--ab-grey-100"),
            borderColor: this.cssColorService.getColor("--ab-grey-100"),
            hoverBackgroundColor:
              this.cssColorService.getColor("--ab-grey-300"),
            hoverBorderColor: this.cssColorService.getColor("--ab-grey-300"),
            barPercentage: 0.85,
            categoryPercentage: 1,
          },
          {
            label: "Rejected errors",
            data: metrics.rejected.count.slice(-10),
            backgroundColor: this.cssColorService.getColor("--ab-blue-400"),
            borderColor: this.cssColorService.getColor("--ab-blue-400"),
            hoverBackgroundColor:
              this.cssColorService.getColor("--ab-blue-300"),
            hoverBorderColor: this.cssColorService.getColor("--ab-blue-300"),
            barPercentage: 0.85,
            categoryPercentage: 1,
          },
        ],
      },
      options: this.MINI_BAR_CHART_OPTIONS,
    };
  }

  // tables sum all 90 segments of data
  private buildProjectTableData(project: any, metrics: IMetrics) {
    project.acceptedNoticeCount = sum(metrics.accepted.count);
    project.rateLimitedNoticeCount = sum(metrics.rateLimited.count);
    project.deployCount = sum(metrics.deploys.count);
    project.newGroupCount = sum(metrics.newGroups.count);

    project.rateLimitedNoticeCountList = metrics.rateLimited.count;
    project.acceptedNoticeCountList = metrics.accepted.count;
    project.newGroupCountList = metrics.newGroups.count;
    project.deployCountList = metrics.deploys.count;
  }
}
